import { Component, inject, OnInit } from '@angular/core';
import { CommonModule, NgOptimizedImage } from '@angular/common';
import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
  AbstractControl,
  ValidationErrors,
} from '@angular/forms';
import { Router, ActivatedRoute, RouterModule } from '@angular/router';
import { AuthService } from '../../services/auth/auth.service';
import { ToastService } from '../../../shared/services/toast.service';

@Component({
  selector: 'app-reset-password',
  standalone: true,
  imports: [CommonModule, NgOptimizedImage, ReactiveFormsModule, RouterModule],
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss',
})
export class ResetPasswordComponent implements OnInit {
  currentYear = new Date().getFullYear();
  resetPasswordForm: FormGroup;
  loading = false;
  resetToken: string | null = null;
  tokenValid = true;
  passwordReset = false;
  redirectUrl: string | null = null;

  showPassword = false;
  showConfirmPassword = false;

  private readonly fb = inject(FormBuilder);
  private readonly authService = inject(AuthService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly toastService = inject(ToastService);

  constructor() {
    this.resetPasswordForm = this.fb.group(
      {
        newPassword: [
          '',
          [
            Validators.required,
            // Validators.minLength(8),
            // this.passwordStrengthValidator
          ],
        ],
        confirmPassword: ['', [Validators.required]],
      },
      { validators: this.passwordMatchValidator },
    );
  }

  ngOnInit() {
    // Get token from route parameters
    this.route.params.subscribe((params) => {
      this.resetToken = params['token'];
      if (!this.resetToken) {
        this.tokenValid = false;
        this.toastService.showError(
          'Invalid reset link. Please request a new password reset.',
        );
      }
    });

    // Get redirect URL from query parameters
    this.route.queryParamMap.subscribe((params) => {
      this.redirectUrl = params.get('redirect-url');
    });
  }

  // Custom validator for password strength
  passwordStrengthValidator(control: AbstractControl): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;

    const hasUpperCase = /[A-Z]/.test(value);
    const hasLowerCase = /[a-z]/.test(value);
    const hasNumeric = /[0-9]/.test(value);
    const hasSpecialChar = /[@$!%*?&]/.test(value);

    const passwordValid =
      hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar;

    return !passwordValid ? { passwordStrength: true } : null;
  }

  // Custom validator for password match
  passwordMatchValidator(group: AbstractControl): ValidationErrors | null {
    const password = group.get('newPassword');
    const confirmPassword = group.get('confirmPassword');

    if (!password || !confirmPassword) return null;

    return password.value === confirmPassword.value
      ? null
      : { passwordMismatch: true };
  }

  onSubmit() {
    if (this.resetPasswordForm.valid && this.resetToken) {
      this.loading = true;
      const { newPassword, confirmPassword } = this.resetPasswordForm.value;

      this.authService
        .resetPassword(this.resetToken, newPassword, confirmPassword)
        .subscribe({
          next: (_data) => {
            this.loading = false;
            this.passwordReset = true;
            this.toastService.showSuccess(
              'Password reset successfully! You can now login with your new password.',
            );
          },
          error: (err) => {
            this.loading = false;
            let errorMessage = 'Failed to reset password. Please try again.';

            if (err.error?.message) {
              errorMessage = err.error.message;
            } else if (err.message) {
              errorMessage = err.message;
            }

            this.toastService.showError(errorMessage);
          },
        });
    } else {
      this.resetPasswordForm.markAllAsTouched();
    }
  }

  togglePasswordVisibility() {
    this.showPassword = !this.showPassword;
  }

  toggleConfirmPasswordVisibility() {
    this.showConfirmPassword = !this.showConfirmPassword;
  }

  goToLogin() {
    if (this.redirectUrl !== null && this.redirectUrl.trim() !== '') {
      void this.router.navigate(['/'], {
        queryParams: { 'redirect-url': this.redirectUrl },
      });
    } else {
      void this.router.navigate(['/']);
    }
  }

  requestNewReset() {
    if (this.redirectUrl !== null && this.redirectUrl.trim() !== '') {
      void this.router.navigate(['/forgot-password'], {
        queryParams: { 'redirect-url': this.redirectUrl },
      });
    } else {
      void this.router.navigate(['/forgot-password']);
    }
  }

  getPasswordStrengthText(): string {
    const password = this.resetPasswordForm.get('newPassword')?.value ?? '';

    if (password.length === 0) return '';

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumeric = /[0-9]/.test(password);
    const hasSpecialChar = /[@$!%*?&]/.test(password);

    const criteria = [hasUpperCase, hasLowerCase, hasNumeric, hasSpecialChar];
    const metCriteria = criteria.filter(Boolean).length;

    if (password.length < 8) return 'Password must be at least 8 characters';
    if (metCriteria < 4)
      return 'Password must contain uppercase, lowercase, number, and special character';

    return 'Strong password';
  }

  getPasswordStrengthClass(): string {
    const password = this.resetPasswordForm.get('newPassword')?.value ?? '';

    if (password.length === 0) return '';

    const hasUpperCase = /[A-Z]/.test(password);
    const hasLowerCase = /[a-z]/.test(password);
    const hasNumeric = /[0-9]/.test(password);
    const hasSpecialChar = /[@$!%*?&]/.test(password);

    const criteria = [hasUpperCase, hasLowerCase, hasNumeric, hasSpecialChar];
    const metCriteria = criteria.filter(Boolean).length;

    if (password.length < 8 || metCriteria < 2) return 'weak';
    if (metCriteria < 4) return 'medium';

    return 'strong';
  }
}
